import { relaunchApi } from '@/logic/relaunchApi'

const SQLITE_CACHE_OPTIONS = {
  // Import the promiser module; it will spawn the worker (bundler-friendly) using locateFile
  workerModuleUrl: '/sqlite-wasm/jswasm/sqlite3-worker1-promiser.mjs',
  workerAssetBase: '/sqlite-wasm/jswasm/',
  databaseFilename: 'chat-translations.sqlite3',
}

const TABLE_SCHEMA_SQL = `
  CREATE TABLE IF NOT EXISTS translations (
    cache_key TEXT PRIMARY KEY,
    target_language TEXT NOT NULL,
    message_text TEXT NOT NULL,
    context TEXT,
    response TEXT NOT NULL,
    created_at INTEGER NOT NULL
  );
`

function normalizeAssetBase(base) {
  if (!base) {
    return ''
  }

  return base.endsWith('/') ? base : `${base}/`
}

function nowInSeconds() {
  return Math.floor(Date.now() / 1000)
}

export async function computeCacheKey(targetLanguage, text, context) {
  const normalized = `${targetLanguage}\u0000${context || ''}\u0000${text}`

  if (typeof window === 'undefined' || !window.crypto || !window.crypto.subtle) {
    return normalized
  }

  try {
    const encoder = new TextEncoder()
    const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(normalized))
    const bytes = Array.from(new Uint8Array(digest))

    return bytes.map((byte) => byte.toString(16).padStart(2, '0')).join('')
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[ChatTranslationCache] Failed to hash cache key, falling back to plain key.', error)

    return normalized
  }
}

export async function initializePromiser() {
  const {
    workerModuleUrl,
    workerAssetBase,
    databaseFilename,
  } = SQLITE_CACHE_OPTIONS

  const module = await import(/* webpackIgnore: true */ workerModuleUrl)
  const sqlite3Worker1Promiser = module.default || module.sqlite3Worker1Promiser

  if (!sqlite3Worker1Promiser) {
    throw new Error('sqlite3Worker1Promiser export not found')
  }

  const assetBase = normalizeAssetBase(workerAssetBase)
  const promiser = await sqlite3Worker1Promiser({
    locateFile: (file) => `${assetBase}${file}`,
  })

  if (!promiser) {
    throw new Error('Failed to obtain sqlite-wasm promiser instance')
  }

  await promiser('open', {
    filename: databaseFilename,
    vfs: 'opfs',
  })

  await promiser('exec', {
    sql: TABLE_SCHEMA_SQL,
  })

  await promiser('exec', {
    sql: 'CREATE UNIQUE INDEX IF NOT EXISTS idx_translations_cache_key ON translations (cache_key)',
  })

  return promiser
}

export async function readFromDatabase(promiser, cacheKey) {
  try {
    const rows = await promiser('exec', {
      sql: 'SELECT response FROM translations WHERE cache_key = $cacheKey LIMIT 1',
      bind: { $cacheKey: cacheKey },
      rowMode: 'object',
      returnValue: 'resultRows',
    })

    if (Array.isArray(rows) && rows.length > 0 && rows[0]?.response) {
      return JSON.parse(rows[0].response)
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[ChatTranslationCache] Failed to read translation from sqlite cache.', error)
  }

  return null
}

export async function writeToDatabase(promiser, cacheKey, payload, translation) {
  try {
    await promiser('exec', {
      sql: `
        INSERT INTO translations (cache_key, target_language, message_text, context, response, created_at)
        VALUES ($cacheKey, $targetLanguage, $messageText, $context, $response, $createdAt)
        ON CONFLICT(cache_key) DO UPDATE SET
          target_language = excluded.target_language,
          message_text = excluded.message_text,
          context = excluded.context,
          response = excluded.response,
          created_at = excluded.created_at
      `,
      bind: {
        $cacheKey: cacheKey,
        $targetLanguage: payload.targetLanguage,
        $messageText: payload.text,
        $context: payload.context ?? null,
        $response: JSON.stringify(translation),
        $createdAt: nowInSeconds(),
      },
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[ChatTranslationCache] Failed to write translation to sqlite cache.', error)
  }
}

export async function fetchTranslationFromApi({ targetLanguage, text, context }) {
  const response = await relaunchApi.post('/global/translation-services/translate', {
    input: text,
    output_language: targetLanguage,
    context: context || null,
  })

  // Response shape: { translation, detectedLanguage }
  return response.data
}
